import { apiClient } from '@/api/client'
import { endpoints } from '@/api/endpoints'
import { ApiResponse } from '@/api/types'

/**
 * Fixed Asset interface
 */
export interface FixedAsset {
  id?: number
  name?: string
  fixedAssetNumber?: string
  assetCategoryId?: number
  assetCategoryName?: string
  purchaseDate?: string
  purchasePrice?: number
  warrantyExpiry?: string
  serialNumber?: string
  costProfitCenterId?: number
  costLocationId?: number
  description?: string
  depreciationStartDate?: string
  depreciationMethod?: string
  effectiveLifeYears?: number
  fixedAssetAccountId?: number
  depreciationAccountId?: number
  expenseAccountId?: number
  yearlyRate?: number
  monthlyDepreciation?: number
  accumulatedDepreciation?: number
  notDepreciableValue?: number
  bookValue?: number
  isSold?: boolean
  sellingValue?: number
  sellingDate?: string
  branchId?: number
  companyId?: number
}

/**
 * Fixed Asset View Model for API
 */
export interface FixedAssetVm {
  fixedAssetId?: number
  fixedAssetNo?: string
  fixedAssetEn?: string
  fixedAssetAr?: string
  purchaseDate?: string
  purchasePrice?: number
  warrantyExpiry?: string
  serialNo?: string
  assetTypeId?: number
  description?: string
  location?: number
  costProfitCenterId?: number
  qrCode?: string
  depreciationStartDate?: string
  costLimit?: number
  residualValue?: number
  lastDepreciationRun?: string
  depreciationMethodId?: number
  monthlyDepreciation?: number
  notDepreciableValue?: number
  accumulatedDepreciation?: number
  rate?: number
  fixedAssetAccount?: number
  depreciationAccount?: number
  expenseAccount?: number
  salesAccount?: number
  gainLossAccount?: number
  bookValue?: number
  isSold?: boolean
  sellingValue?: number
  sellingDate?: string
  effectiveLifeYears?: number
  companyId?: number
  branchId?: number
  portalId?: number
  createdDate?: string
  createdBy?: number
  modifiedDate?: string
  modifiedBy?: number
}

/**
 * Fixed Asset List Response
 */
export interface FixedAssetListResponse {
  totalRecords?: number
  pageNumber?: number
  pageSize?: number
  data?: FixedAsset[]
}

/**
 * Lookup interface
 */
export interface Lookup {
  id: number
  lookupNameEn?: string
  lookupNameAr?: string
  name?: string
  nameEn?: string
  nameAr?: string
}

/**
 * Fixed Asset management service
 * Handles all fixed asset-related API operations
 */
export class FixedAssetService {
  /**
   * Get paginated list of fixed assets for a branch
   */
  async getAllAssets(
    branchId: number,
    pageNumber: number,
    pageSize: number
  ): Promise<FixedAssetListResponse> {
    const url = endpoints.fixedAssets.getAll(branchId, pageNumber, pageSize)
    const response = await apiClient.get<ApiResponse<FixedAssetListResponse> | FixedAssetListResponse | any>(url)
    const payload = response.data

    // Helper to map assets array
    const mapAssets = (assets: any[]): FixedAsset[] => {
      return assets.map(asset => this.mapAssetData(asset))
    }

    if (payload && typeof payload === 'object') {
      if ('success' in payload && 'data' in payload) {
        const data = payload.data
        if (Array.isArray(data)) {
          return {
            totalRecords: payload.totalRecords ?? data.length,
            pageNumber: payload.pageNumber ?? pageNumber,
            pageSize: payload.pageSize ?? pageSize,
            data: mapAssets(data),
          }
        }
        // Common shape: { success, data: { totalRecords, pageNumber, pageSize, data: [...] } }
        if (data && typeof data === 'object' && Array.isArray((data as any).data)) {
          return {
            totalRecords: (data as any).totalRecords ?? payload.totalRecords ?? (data as any).data.length ?? 0,
            pageNumber: (data as any).pageNumber ?? payload.pageNumber ?? pageNumber,
            pageSize: (data as any).pageSize ?? payload.pageSize ?? pageSize,
            data: mapAssets((data as any).data),
          }
        }
        return data as FixedAssetListResponse
      }

      if ('data' in payload && Array.isArray(payload.data)) {
        return {
          totalRecords: payload.totalRecords ?? payload.data.length,
          pageNumber: payload.pageNumber ?? pageNumber,
          pageSize: payload.pageSize ?? pageSize,
          data: mapAssets(payload.data),
        }
      }
    }

    if (Array.isArray(payload)) {
      return {
        totalRecords: payload.length,
        pageNumber,
        pageSize,
        data: mapAssets(payload),
      }
    }

    return {
      totalRecords: payload?.totalRecords ?? 0,
      pageNumber: payload?.pageNumber ?? pageNumber,
      pageSize: payload?.pageSize ?? pageSize,
      data: payload?.data ? mapAssets(payload.data) : [],
    }
  }

  /**
   * Get fixed asset by ID
   */
  async getAssetById(id: number, branchId: number): Promise<FixedAsset> {
    const url = endpoints.fixedAssets.getById(id, branchId)
    const response = await apiClient.get<ApiResponse<FixedAsset> | FixedAsset | any>(url)
    
    let assetData: any
    
    if ('data' in response.data && 'success' in response.data) {
      assetData = response.data.data
    } else if ('data' in response.data) {
      assetData = response.data.data
    } else {
      assetData = response.data
    }
    
    return this.mapAssetData(assetData)
  }

  /**
   * Get assets by type
   */
  async getAssetsByType(id: number, branchId: number): Promise<FixedAsset[]> {
    const url = endpoints.fixedAssets.getByType(id, branchId)
    const response = await apiClient.get<ApiResponse<FixedAsset[]> | FixedAsset[] | any>(url)
    
    let assetsData: any[] = []
    
    if (Array.isArray(response.data)) {
      assetsData = response.data
    } else if ('data' in response.data && Array.isArray(response.data.data)) {
      assetsData = response.data.data
    } else if ('data' in response.data && 'success' in response.data) {
      assetsData = Array.isArray(response.data.data) ? response.data.data : []
    }
    
    return assetsData.map(asset => this.mapAssetData(asset))
  }

  /**
   * Save fixed asset (insert)
   */
  async saveAsset(asset: FixedAssetVm): Promise<FixedAsset> {
    const url = endpoints.fixedAssets.save
    console.log('🔵 [FixedAssetService.saveAsset] URL:', url)
    console.log('🔵 [FixedAssetService.saveAsset] Payload:', JSON.stringify(asset, null, 2))
    
    try {
      const response = await apiClient.post<ApiResponse<FixedAsset> | FixedAsset>(url, asset)
      console.log('🔵 [FixedAssetService.saveAsset] Response:', response.data)
      
      if ('data' in response.data && 'success' in response.data) {
        return this.mapAssetData((response.data as ApiResponse<FixedAsset>).data)
      }
      
      return this.mapAssetData(response.data as FixedAsset)
    } catch (error: any) {
      console.error('❌ [FixedAssetService.saveAsset] Error:', error)
      console.error('❌ [FixedAssetService.saveAsset] Error Response:', error.response?.data)
      throw error
    }
  }

  /**
   * Update fixed asset
   */
  async updateAsset(asset: FixedAssetVm): Promise<FixedAsset> {
    const url = endpoints.fixedAssets.update
    console.log('🔵 [FixedAssetService.updateAsset] URL:', url)
    console.log('🔵 [FixedAssetService.updateAsset] Payload:', JSON.stringify(asset, null, 2))
    
    try {
      const response = await apiClient.post<ApiResponse<FixedAsset> | FixedAsset>(url, asset)
      console.log('🔵 [FixedAssetService.updateAsset] Response:', response.data)
      
      if ('data' in response.data && 'success' in response.data) {
        return this.mapAssetData((response.data as ApiResponse<FixedAsset>).data)
      }
      
      return this.mapAssetData(response.data as FixedAsset)
    } catch (error: any) {
      console.error('❌ [FixedAssetService.updateAsset] Error:', error)
      console.error('❌ [FixedAssetService.updateAsset] Error Response:', error.response?.data)
      throw error
    }
  }

  /**
   * Delete fixed asset
   */
  async deleteAsset(id: number): Promise<ApiResponse<void>> {
    const url = endpoints.fixedAssets.delete(id)
    const response = await apiClient.delete<ApiResponse<void>>(url)
    return response.data
  }

  /**
   * Get depreciation methods
   */
  async getDepMethods(): Promise<any[]> {
    const url = endpoints.fixedAssets.getDepMethods
    const response = await apiClient.get<ApiResponse<any[]> | any[] | any>(url)
    
    if (Array.isArray(response.data)) {
      return response.data
    } else if ('data' in response.data && Array.isArray(response.data.data)) {
      return response.data.data
    }
    
    return []
  }

  /**
   * Get depreciation method lookups
   */
  async getDepreciationMethodLookups(): Promise<Lookup[]> {
    const url = endpoints.fixedAssets.getDepreciationMethodLookups
    const response = await apiClient.get<ApiResponse<Lookup[]> | Lookup[] | any>(url)
    
    let lookupsData: any[] = []
    
    if (Array.isArray(response.data)) {
      lookupsData = response.data
    } else if ('data' in response.data && Array.isArray(response.data.data)) {
      lookupsData = response.data.data
    }
    
    return lookupsData.map((item: any) => ({
      id: item.id ?? item.Id ?? item.lookupId ?? item.lookupID ?? item.lookupID ?? 0,
      lookupNameEn: item.lookupNameEn || item.nameEn || item.name || '',
      lookupNameAr: item.lookupNameAr || item.nameAr || '',
    }))
  }

  /**
   * Get asset category lookups
   */
  async getAssetCategoryLookups(): Promise<Lookup[]> {
    const url = endpoints.fixedAssets.getAssetCategoryLookups
    const response = await apiClient.get<ApiResponse<Lookup[]> | Lookup[] | any>(url)
    
    let lookupsData: any[] = []
    
    if (Array.isArray(response.data)) {
      lookupsData = response.data
    } else if ('data' in response.data && Array.isArray(response.data.data)) {
      lookupsData = response.data.data
    }
    
    return lookupsData.map((item: any) => ({
      id: item.id ?? item.Id ?? item.lookupId ?? item.lookupID ?? 0,
      lookupNameEn: item.lookupNameEn || item.nameEn || item.name || '',
      lookupNameAr: item.lookupNameAr || item.nameAr || '',
    }))
  }

  /**
   * Get asset types
   */
  async getAssetTypes(): Promise<Lookup[]> {
    const url = endpoints.fixedAssets.getAssetTypes
    const response = await apiClient.get<ApiResponse<Lookup[]> | Lookup[] | any>(url)
    
    let lookupsData: any[] = []
    
    if (Array.isArray(response.data)) {
      lookupsData = response.data
    } else if ('data' in response.data && Array.isArray(response.data.data)) {
      lookupsData = response.data.data
    }
    
    return lookupsData.map((item: any) => ({
      id: item.id || 0,
      lookupNameEn: item.lookupNameEn || item.nameEn || item.name || '',
      lookupNameAr: item.lookupNameAr || item.nameAr || '',
    }))
  }

  /**
   * Map API response to FixedAsset interface
   */
  private mapAssetData(assetData: any): FixedAsset {
    return {
      id: assetData.fixedAssetId || assetData.id || 0,
      name: assetData.fixedAssetEn || assetData.name || '',
      fixedAssetNumber: assetData.fixedAssetNo || assetData.fixedAssetNumber || '',
      assetCategoryId: assetData.assetTypeId || assetData.assetCategoryId || 0,
      assetCategoryName: assetData.assetTypeName || assetData.assetCategoryName || assetData.assetType || '',
      purchaseDate: assetData.purchaseDate || '',
      purchasePrice: assetData.purchasePrice || 0,
      warrantyExpiry: assetData.warrantyExpiry || '',
      serialNumber: assetData.serialNo || assetData.serialNumber || '',
      costProfitCenterId: assetData.costProfitCenterId || 0,
      costLocationId: assetData.location || assetData.costLocationId || 0,
      description: assetData.description || '',
      depreciationStartDate: assetData.depreciationStartDate || '',
      depreciationMethod: assetData.depreciationMethodId || assetData.depreciationMethod || '',
      effectiveLifeYears: assetData.effectiveLifeYears || 0,
      fixedAssetAccountId: assetData.fixedAssetAccount || assetData.fixedAssetAccountId || 0,
      depreciationAccountId: assetData.depreciationAccount || assetData.depreciationAccountId || 0,
      expenseAccountId: assetData.expenseAccount || assetData.expenseAccountId || 0,
      yearlyRate: assetData.rate || assetData.yearlyRate || 0,
      monthlyDepreciation: assetData.monthlyDepreciation || 0,
      accumulatedDepreciation: assetData.accumulatedDepreciation || 0,
      notDepreciableValue: assetData.notDepreciableValue || 0,
      bookValue: assetData.bookValue || 0,
      isSold: assetData.isSold || false,
      sellingValue: assetData.sellingValue || 0,
      sellingDate: assetData.sellingDate || '',
      branchId: assetData.branchId,
      companyId: assetData.companyId,
    } as FixedAsset
  }

  /**
   * Run depreciation for a specific month/year
   */
  async runDepreciation(branchId: number, monthYear: string): Promise<ApiResponse<any>> {
    const url = endpoints.fixedAssets.depreciationRun(branchId, encodeURIComponent(monthYear))
    const response = await apiClient.post<ApiResponse<any>>(url, {})
    return response.data
  }

  /**
   * Get paginated list of asset disposals for a branch
   * Note: API endpoint may not exist yet, returns empty list for now
   */
  async getAllDisposals(
    branchId: number,
    pageNumber: number,
    pageSize: number
  ): Promise<{ totalRecords: number; pageNumber: number; pageSize: number; data: any[] }> {
    try {
      const url = endpoints.fixedAssets.disposal.getAll(branchId, pageNumber, pageSize)
      const response = await apiClient.get<ApiResponse<any> | any>(url)
      const payload = response.data

      if (payload && typeof payload === 'object') {
        if ('success' in payload && 'data' in payload) {
          return {
            totalRecords: payload.data.totalRecords || 0,
            pageNumber: payload.data.pageNumber || pageNumber,
            pageSize: payload.data.pageSize || pageSize,
            data: payload.data.data || [],
          }
        }

        if ('data' in payload && Array.isArray(payload.data)) {
          return {
            totalRecords: payload.totalRecords || payload.data.length,
            pageNumber: payload.pageNumber || pageNumber,
            pageSize: payload.pageSize || pageSize,
            data: payload.data,
          }
        }
      }

      if (Array.isArray(payload)) {
        return {
          totalRecords: payload.length,
          pageNumber,
          pageSize,
          data: payload,
        }
      }

      return {
        totalRecords: payload?.totalRecords || 0,
        pageNumber: payload?.pageNumber || pageNumber,
        pageSize: payload?.pageSize || pageSize,
        data: payload?.data || [],
      }
    } catch (error: any) {
      // If API doesn't exist yet, return empty list
      console.warn('Asset Disposal API not available yet:', error)
      return {
        totalRecords: 0,
        pageNumber,
        pageSize,
        data: [],
      }
    }
  }

  /**
   * Save asset disposal
   */
  async saveDisposal(disposalData: {
    fixedAssetId: number
    fixedAssetAccount: number
    collectionAccount: number
    gainLossAccount: number
    saleAmount: number
    saleDate: string
  }): Promise<ApiResponse<any>> {
    const url = endpoints.fixedAssets.disposal.save
    const response = await apiClient.post<ApiResponse<any>>(url, disposalData)
    return response.data
  }
}

export const fixedAssetService = new FixedAssetService()

